/**
 * 스키마 비교 — ① 비교 대상(연결·스키마·범위) → ② 오브젝트(종류 · 물리 · 통계) → ③ 비교 결과(종류별 탭) → ④ 동기화 스크립트,
 * 그리고 비교 기준(①·② 의 선택을 이름 붙여 저장 · ① 에서 불러온다). docs/features/28-schema-compare.md
 *
 * 틀은 SQL 품질 검사와 같다(왼쪽 목록 [비교 | 비교 기준] · 단계 리본). LLM 을 부르지 않고 DB 를 바꾸지 않는다 —
 * 스크립트는 글이고, 실행은 사람이 편집기에서 한다(「편집기에서 열기」).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiColumns, FiPlus, FiTrash2, FiSliders, FiPlay, FiSquare, FiCopy, FiDownload, FiExternalLink, FiChevronRight, FiChevronDown } from 'react-icons/fi';
import AppModal from '../common/AppModal';
import { useConfirm } from '../ConfirmDialog';
import { usePrompt } from '../PromptDialog';
import { formatApiError } from '../../utils/formatters';
import { storedTime } from '../../utils/storedTime';
import * as api from '../../services/schemaCompare';
import '../sqlReview/SqlReviewModal.css';
import '../sqlAuthor/SqlAuthorModal.css';
import '../sqlQuality/SqlQualityModal.css';
import './SchemaCompareModal.css';
import memoWindow from '../common/memoWindow';
import WorkflowHead, { WorkflowRibbon } from '../common/WorkflowHead';

const TABS = ['target', 'objects', 'results', 'script'];
const TYPES = ['table', 'constraint', 'index', 'view', 'sequence', 'plsql'];
const STATUSES = ['diff', 'onlySource', 'onlyTarget', 'same'];
const SUPPORTED = ['oracle'];
const fmt = (v) => (v == null ? '-' : Number(v).toLocaleString('en-US'));
const emptySpec = (connId) => ({
  source: { connectionId: connId || '', owner: '' },
  target: { connectionId: connId || '', owner: '' },
  scope: { include: '', exclude: '', condition: '' },
  types: ['table', 'constraint', 'index'],
  options: { physical: false, stats: false },
});
/** 값 칸 — 배열·참거짓도 읽게 */
const show = (v, t) => {
  if (v == null) return '—';
  if (Array.isArray(v)) return v.join(', ') || '—';
  if (typeof v === 'boolean') return v ? t('common:scmp.yes') : t('common:scmp.no');
  return String(v);
};
/** 「column ENAME.type」 → 사람 말 */
function whatLabel(what, t) {
  const m = /^column (\S+?)(?:\.(\w+))?$/.exec(what);
  if (m) return m[2] ? `${t('common:scmp.what.column')} ${m[1]} · ${t(`common:scmp.what.${m[2]}`, { defaultValue: m[2] })}` : `${t('common:scmp.what.column')} ${m[1]}`;
  const p = /^partition\.(\w+)$/.exec(what);
  if (p) return `${t('common:scmp.what.partition')} · ${t(`common:scmp.what.${p[1]}`, { defaultValue: p[1] })}`;
  return t(`common:scmp.what.${what}`, { defaultValue: what });
}

/** 한쪽(원본·대상)의 연결·스키마 고르기 */
function SidePicker({ label, side, conns, onChange }) {
  const { t } = useTranslation();
  const [owners, setOwners] = useState(null);
  const [showSys, setShowSys] = useState(false);
  const [loading, setLoading] = useState(false);
  const { connectionId } = side;
  useEffect(() => {
    if (!connectionId) { setOwners(null); return undefined; }
    let alive = true;
    setLoading(true);
    api.listSchemas(connectionId)
      .then((r) => {
        if (!alive) return;
        setOwners(r.owners || []);
        // 스키마를 아직 안 골랐으면 그 연결의 현재 스키마로
        if (!side.owner && r.current) onChange({ ...side, owner: r.current });
      })
      .catch((err) => { if (alive) { setOwners([]); toast.error(formatApiError(err, t)); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 연결이 바뀔 때만 읽는다
  }, [connectionId]);
  const list = (owners || []).filter(o => showSys || !o.system || o.name === side.owner);
  return (
    <div className="stn-box scmp-side">
      <b>{label}</b>
      <label className="stn-field"><span>{t('common:scmp.connection')}</span>
        <select value={side.connectionId} onChange={e => onChange({ connectionId: e.target.value, owner: '' })}>
          <option value="">{t('common:scmp.pickConnection')}</option>
          {conns.map(c => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
        </select>
      </label>
      <label className="stn-field"><span>{t('common:scmp.schema')}</span>
        <select value={side.owner} onChange={e => onChange({ ...side, owner: e.target.value })} disabled={!side.connectionId || loading}>
          <option value="">{loading ? t('common:scmp.loadingSchemas') : t('common:scmp.pickSchema')}</option>
          {list.map(o => <option key={o.name} value={o.name}>{o.name}</option>)}
        </select>
      </label>
      {owners && owners.some(o => o.system) && (
        <label className="stn-row stn-note"><input type="checkbox" checked={showSys} onChange={e => setShowSys(e.target.checked)} /> {t('common:scmp.showSystem')}</label>
      )}
    </div>
  );
}
SidePicker.propTypes = { label: PropTypes.string.isRequired, side: PropTypes.object.isRequired, conns: PropTypes.array.isRequired, onChange: PropTypes.func.isRequired };

/** 결과 한 줄 — 누르면 차이 표(본문은 줄 단위) */
function ResultRow({ e, open, onToggle }) {
  const { t } = useTranslation();
  const nDiff = e.diffs.length;
  return (
    <li className={`scmp-item st-${e.status}${open ? ' open' : ''}`}>
      <button type="button" className="scmp-item-head" onClick={onToggle} disabled={e.status === 'same'} aria-expanded={open}>
        {e.status === 'same' ? <span className="scmp-caret" /> : (open ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />)}
        <span className="scmp-name mono">{e.name}</span>
        {e.targetName && <span className="stn-note mono">→ {e.targetName}</span>}
        <span className={`scmp-badge st-${e.status}`}>{t(`common:scmp.status.${e.status}`)}</span>
        {e.status === 'diff' && <span className="stn-note">{t('common:scmp.diffCount', { n: nDiff })}</span>}
      </button>
      {open && e.status === 'diff' && (
        <div className="scmp-detail sqq-fade">
          <table className="stn-table scmp-diff">
            <thead><tr><th>{t('common:scmp.cols.level')}</th><th>{t('common:scmp.cols.what')}</th><th>{t('common:scmp.cols.source')}</th><th>{t('common:scmp.cols.target')}</th></tr></thead>
            <tbody>
              {e.diffs.map((d, i) => (
                // eslint-disable-next-line react/no-array-index-key -- 차이 목록은 순서가 곧 키다
                <tr key={i} className={`lv-${d.level}`}>
                  <td><span className={`scmp-lv lv-${d.level}`}>{t(`common:scmp.level.${d.level}`)}</span></td>
                  <td>{whatLabel(d.what, t)}{d.kind ? ` (${t(`common:scmp.status.${d.kind}`)})` : ''}</td>
                  {d.textDiff
                    ? (
                      <td colSpan={2}>
                        <pre className="scmp-textdiff mono">{d.textDiff.lines.map((l, k) => (
                          // eslint-disable-next-line react/no-array-index-key -- 줄 순서가 곧 키다
                          <span key={k} className={`td-${l.op === '+' ? 'add' : l.op === '-' ? 'del' : l.op === '…' ? 'gap' : 'same'}`}>{l.op === '…' ? '…' : `${l.op === ' ' ? ' ' : (l.op === '-' ? '-' : '+')} ${l.text}`}{'\n'}</span>
                        ))}</pre>
                        <span className="stn-note">{t('common:scmp.textDiffHint')}</span>
                      </td>
                    )
                    : <><td className="mono">{show(d.source, t)}</td><td className="mono">{show(d.target, t)}</td></>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && e.status !== 'diff' && e.status !== 'same' && <div className="scmp-detail stn-note sqq-fade">{t(`common:scmp.onlyHint.${e.status}`)}</div>}
    </li>
  );
}
ResultRow.propTypes = { e: PropTypes.object.isRequired, open: PropTypes.bool, onToggle: PropTypes.func.isRequired };

const SchemaCompareModal = ({ open, onClose, connections, defaultConnectionId, onOpenInEditor, restore, onView }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const conns = useMemo(() => (connections || []).filter(c => SUPPORTED.includes(c.dbType || 'oracle')), [connections]);
  const firstConn = defaultConnectionId && conns.some(c => c.id === defaultConnectionId) ? defaultConnectionId : '';
  const [side, setSide] = useState('jobs');
  const [jobs, setJobs] = useState([]);
  const [criteriaList, setCriteriaList] = useState([]);
  const [job, setJob] = useState(null);
  const [draft, setDraft] = useState(() => emptySpec(firstConn));
  const [title, setTitle] = useState('');
  const [criteriaId, setCriteriaId] = useState(null);
  const [step, setStep] = useState('target');
  const [busy, setBusy] = useState(null);
  const [typeTab, setTypeTab] = useState('table');
  const [filter, setFilter] = useState('changed');
  const [expanded, setExpanded] = useState(() => new Set());
  const [script, setScript] = useState(null);
  const [scriptOpts, setScriptOpts] = useState({ drop: false, physical: true });

  const reload = useCallback(async () => {
    try { setJobs((await api.listJobs()).jobs || []); } catch (_e) { setJobs([]); }
  }, []);
  const reloadCriteria = useCallback(async () => {
    try { setCriteriaList((await api.listCriteria()).criteria || []); } catch (_e) { setCriteriaList([]); }
  }, []);
  useEffect(() => { if (open) { reload(); reloadCriteria(); } }, [open, reload, reloadCriteria]);

  const showJob = useCallback((j, wantStep) => {
    setJob(j); setDraft(j.spec); setTitle(j.title); setCriteriaId(j.criteriaId || null);
    setExpanded(new Set()); setScript(null); setFilter('changed');
    setTypeTab((j.spec.types || TYPES)[0]);
    setStep(wantStep && TABS.includes(wantStep) ? wantStep : 'results');
  }, []);
  const openJob = useCallback(async (id, wantStep) => {
    try { showJob((await api.getJob(id)).job, wantStep); } catch (err) { toast.error(formatApiError(err, t)); }
  }, [showJob, t]);

  // 새로고침 뒤 — 보던 비교·단계로 한 번만
  const [viewReady, setViewReady] = useState(false);
  useEffect(() => {
    if (!open || viewReady) return;
    if (restore && restore.id) openJob(restore.id, restore.step).finally(() => setViewReady(true));
    else setViewReady(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 처음 열릴 때 한 번
  }, [open]);
  const viewJobId = job ? job.id : null;
  useEffect(() => { if (open && viewReady && onView) onView({ id: viewJobId, step }); }, [open, viewReady, viewJobId, step, onView]);

  const startNew = (spec, cid, name) => {
    setSide('jobs'); setJob(null); setDraft(spec || emptySpec(firstConn)); setCriteriaId(cid || null); setTitle(name || '');
    setScript(null); setStep('target');
  };
  const setSideSpec = (k) => (v) => setDraft(d => ({ ...d, [k]: v }));
  const setScope = (k) => (e) => setDraft(d => ({ ...d, scope: { ...d.scope, [k]: e.target.value } }));
  const toggleType = (ty) => setDraft(d => ({ ...d, types: d.types.includes(ty) ? d.types.filter(x => x !== ty) : TYPES.filter(x => x === ty || d.types.includes(x)) }));
  const ready = draft.source.connectionId && draft.source.owner && draft.target.connectionId && draft.target.owner;
  const same = ready && draft.source.connectionId === draft.target.connectionId && draft.source.owner === draft.target.owner;

  const run = async (again) => {
    setBusy('run');
    try {
      const r = await api.runCompare({ spec: draft, title, criteriaId, jobId: again && job ? job.id : undefined });
      showJob(r.job, 'results'); reload();
      toast.success(t('common:scmp.done', { ms: fmt(r.job.result && r.job.result.elapsedMs) }));
    } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(null); }
  };
  const stop = () => api.cancelCompare().catch(() => {});

  const saveAsCriteria = async () => {
    const cur = criteriaId ? criteriaList.find(c => c.id === criteriaId) : null;
    const name = await prompt({ title: t('common:scmp.saveCriteriaTitle'), message: t('common:scmp.saveCriteriaBody'), defaultValue: cur ? cur.name : (title || `${draft.source.owner} ↔ ${draft.target.owner}`), confirmText: t('common:scmp.saveCriteria') });
    if (!name) return;
    try {
      const overwrite = cur && cur.name === name.trim();
      const r = await api.saveCriteria({ id: overwrite ? cur.id : undefined, name: name.trim(), spec: draft });
      setCriteriaId(r.criteria.id); reloadCriteria();
      toast.success(t(overwrite ? 'common:scmp.criteriaUpdated' : 'common:scmp.criteriaSaved', { name: r.criteria.name }));
    } catch (err) { toast.error(formatApiError(err, t)); }
  };
  const loadCriteria = (c) => { startNew({ ...emptySpec(firstConn), ...c.spec }, c.id, ''); toast.info(t('common:scmp.criteriaLoaded', { name: c.name })); };
  const removeCriteria = async (c) => {
    const ok = await confirm({ title: t('common:scmp.deleteCriteriaTitle'), message: t('common:scmp.deleteCriteriaBody', { name: c.name }), confirmText: t('common:ui.delete'), variant: 'danger' });
    if (!ok) return;
    try { await api.deleteCriteria(c.id); if (criteriaId === c.id) setCriteriaId(null); reloadCriteria(); } catch (err) { toast.error(formatApiError(err, t)); }
  };
  const removeJob = async () => {
    const ok = await confirm({ title: t('common:scmp.deleteJobTitle'), message: t('common:scmp.deleteJobBody', { title: job.title }), confirmText: t('common:ui.delete'), variant: 'danger' });
    if (!ok) return;
    try { await api.deleteJob(job.id); startNew(); reload(); } catch (err) { toast.error(formatApiError(err, t)); }
  };

  // 스크립트 — 단계에 들어오거나 선택을 바꿀 때 다시 만든다
  useEffect(() => {
    if (!open || !job || step !== 'script') return undefined;
    let alive = true;
    setScript(null);
    api.getScript(job.id, scriptOpts).then((r) => { if (alive) setScript(r); }).catch((err) => { if (alive) toast.error(formatApiError(err, t)); });
    return () => { alive = false; };
  }, [open, job, step, scriptOpts, t]);
  const copyScript = async () => {
    try { await navigator.clipboard.writeText(script.text); toast.success(t('common:scmp.copied')); } catch (_e) { toast.error(t('common:scmp.copyFailed')); }
  };
  const downloadScript = () => {
    const blob = new Blob([script.text], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `schema-sync-${(job.title || job.id).replace(/[\\/:*?"<>|\s]+/g, '_').slice(0, 60)}.sql`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  // ── 단계 ──────────────────────────────────────────────
  const renderTarget = () => (
    <div className="stn-step">
      <div className="stn-row">
        <select value="" onChange={e => { const c = criteriaList.find(x => x.id === e.target.value); if (c) loadCriteria(c); }} aria-label={t('common:scmp.loadCriteria')}>
          <option value="">{criteriaList.length ? t('common:scmp.loadCriteria') : t('common:scmp.noCriteria')}</option>
          {criteriaList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {criteriaId && <span className="stn-note">{t('common:scmp.fromCriteria', { name: (criteriaList.find(c => c.id === criteriaId) || {}).name || '' })}</span>}
      </div>
      <div className="stn-grid2">
        <SidePicker label={t('common:scmp.source')} side={draft.source} conns={conns} onChange={setSideSpec('source')} />
        <SidePicker label={t('common:scmp.target')} side={draft.target} conns={conns} onChange={setSideSpec('target')} />
      </div>
      {!conns.length && <span className="sau-notice">{t('common:scmp.noOracle')}</span>}
      {same && <span className="sau-notice">{t('common:scmp.sameSides')}</span>}
      <div className="stn-box">
        <b>{t('common:scmp.scope')}</b>
        <div className="stn-grid2">
          <label className="stn-field"><span>{t('common:scmp.include')}</span><input className="mono" value={draft.scope.include} onChange={setScope('include')} placeholder={t('common:scmp.includePh')} /></label>
          <label className="stn-field"><span>{t('common:scmp.exclude')}</span><input className="mono" value={draft.scope.exclude} onChange={setScope('exclude')} placeholder={t('common:scmp.excludePh')} /></label>
        </div>
        <label className="stn-field"><span>{t('common:scmp.condition')}</span>
          <input className="mono" value={draft.scope.condition} onChange={setScope('condition')} placeholder={t('common:scmp.conditionPh')} />
        </label>
        <span className="stn-note">{t('common:scmp.scopeHint')}</span>
      </div>
      <div className="stn-actions">
        <button type="button" className="app-btn app-btn-primary" onClick={() => setStep('objects')} disabled={!ready || same}>{t('common:scmp.next')}</button>
      </div>
    </div>
  );

  const renderObjects = () => (
    <div className="stn-step">
      <div className="stn-box">
        <b>{t('common:scmp.types')}</b>
        <div className="scmp-types">
          {TYPES.map(ty => (
            <label key={ty} className={`sau-mode-opt${draft.types.includes(ty) ? ' on' : ''}`}>
              <input type="checkbox" checked={draft.types.includes(ty)} onChange={() => toggleType(ty)} />
              <b>{t(`common:scmp.type.${ty}`)}</b><span className="stn-note">{t(`common:scmp.typeHint.${ty}`)}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="stn-box">
        <b>{t('common:scmp.levels')}</b>
        <label className="stn-row"><input type="checkbox" checked disabled /> <span className="scmp-lv lv-logical">{t('common:scmp.level.logical')}</span> <span className="stn-note">{t('common:scmp.levelHint.logical')}</span></label>
        <label className="stn-row"><input type="checkbox" checked={draft.options.physical} onChange={e => setDraft(d => ({ ...d, options: { ...d.options, physical: e.target.checked } }))} /> <span className="scmp-lv lv-physical">{t('common:scmp.level.physical')}</span> <span className="stn-note">{t('common:scmp.levelHint.physical')}</span></label>
        <label className="stn-row"><input type="checkbox" checked={draft.options.stats} onChange={e => setDraft(d => ({ ...d, options: { ...d.options, stats: e.target.checked } }))} /> <span className="scmp-lv lv-stats">{t('common:scmp.level.stats')}</span> <span className="stn-note">{t('common:scmp.levelHint.stats')}</span></label>
      </div>
      <label className="stn-field"><span>{t('common:scmp.title')}</span><input value={title} onChange={e => setTitle(e.target.value)} placeholder={t('common:scmp.titlePh', { a: draft.source.owner || '…', b: draft.target.owner || '…' })} /></label>
      {busy === 'run' && <div className="stn-progress">{t('common:scmp.running')}</div>}
      <div className="stn-actions">
        <button type="button" className="app-btn" onClick={() => setStep('target')}>{t('common:scmp.prev')}</button>
        {busy === 'run'
          ? <button type="button" className="app-btn app-btn-danger" onClick={stop}><FiSquare size={12} /> {t('common:scmp.stop')}</button>
          : (
            <>
              {job && <button type="button" className="app-btn" onClick={() => run(true)} disabled={!ready || same || !draft.types.length}><FiPlay size={12} /> {t('common:scmp.rerun')}</button>}
              <button type="button" className="app-btn app-btn-primary" onClick={() => run(false)} disabled={!ready || same || !draft.types.length}><FiPlay size={12} /> {t(job ? 'common:scmp.runAsNew' : 'common:scmp.run')}</button>
            </>
          )}
      </div>
    </div>
  );

  const renderResults = () => {
    const res = (job.result && job.result.results) || {};
    const types = (job.spec.types || TYPES).filter(ty => res[ty]);
    const cur = types.includes(typeTab) ? typeTab : types[0];
    const list = (res[cur] || []).filter(e => (filter === 'all' ? true : filter === 'changed' ? e.status !== 'same' : e.status === filter));
    const sum = job.summary || {};
    const toggle = (k) => setExpanded(s => { const x = new Set(s); if (x.has(k)) x.delete(k); else x.add(k); return x; });
    return (
      <div className="stn-step">
        <div className="stn-row stn-note">
          {t('common:scmp.resultLine', { a: `${job.source.owner}@${job.source.connName}`, b: `${job.target.owner}@${job.target.connName}`, at: storedTime(job.updatedAt, 'min') })}
          {job.spec.options.physical && <span className="scmp-lv lv-physical">{t('common:scmp.level.physical')}</span>}
          {job.spec.options.stats && <span className="scmp-lv lv-stats">{t('common:scmp.level.stats')}</span>}
        </div>
        {(job.notes || []).length > 0 && (
          <div className="sau-notice">{t('common:scmp.notes')} <span className="mono">{job.notes.slice(0, 6).join(' · ')}</span></div>
        )}
        <nav className="stn-ribbon scmp-typebar" role="tablist" aria-label={t('common:scmp.types')}>
          {types.map(ty => {
            const s = sum[ty] || {};
            const n = (s.diff || 0) + (s.onlySource || 0) + (s.onlyTarget || 0);
            return (
              <button key={ty} type="button" role="tab" aria-selected={cur === ty} className={`stn-tab${cur === ty ? ' on' : ''}`} onClick={() => { setTypeTab(ty); setExpanded(new Set()); }}>
                {t(`common:scmp.type.${ty}`)} <span className={`scmp-count${n ? ' has' : ''}`}>{n}</span>
              </button>
            );
          })}
        </nav>
        <div className="stn-row scmp-filter" role="group" aria-label={t('common:scmp.filter')}>
          {['changed', ...STATUSES, 'all'].map(f => {
            const s = sum[cur] || {};
            const n = f === 'all' ? STATUSES.reduce((a, k) => a + (s[k] || 0), 0) : f === 'changed' ? (s.diff || 0) + (s.onlySource || 0) + (s.onlyTarget || 0) : (s[f] || 0);
            return <button key={f} type="button" className={`app-btn${filter === f ? ' on' : ''}`} aria-pressed={filter === f} onClick={() => setFilter(f)}>{t(`common:scmp.filters.${f}`)} {fmt(n)}</button>;
          })}
        </div>
        {job.result && job.result.truncated && job.result.truncated[cur] > 0 && <span className="sau-notice">{t('common:scmp.truncated', { n: fmt(job.result.truncated[cur]) })}</span>}
        <ul className="scmp-list" key={`${cur}-${filter}`}>
          {list.map(e => <ResultRow key={`${e.status}:${e.name}`} e={e} open={expanded.has(e.name)} onToggle={() => toggle(e.name)} />)}
          {!list.length && <li className="stn-empty">{t('common:scmp.emptyList')}</li>}
        </ul>
        <div className="stn-actions">
          <button type="button" className="app-btn app-btn-primary" onClick={() => setStep('script')}>{t('common:scmp.toScript')}</button>
        </div>
      </div>
    );
  };

  const renderScript = () => (
    <div className="stn-step">
      <div className="stn-row stn-between">
        <span className="stn-row">
          <label className="stn-row"><input type="checkbox" checked={scriptOpts.drop} onChange={e => setScriptOpts(o => ({ ...o, drop: e.target.checked }))} /> {t('common:scmp.dropExtras')}</label>
          <label className="stn-row" title={job.spec.options.physical ? undefined : t('common:scmp.physicalOffHint')}>
            <input type="checkbox" checked={scriptOpts.physical && job.spec.options.physical} disabled={!job.spec.options.physical} onChange={e => setScriptOpts(o => ({ ...o, physical: e.target.checked }))} /> {t('common:scmp.physicalStmts')}
          </label>
        </span>
        <span className="stn-row">
          {onOpenInEditor && <button type="button" className="app-btn app-btn-primary" disabled={!script} onClick={() => onOpenInEditor(script.text)}><FiExternalLink size={12} /> {t('common:scmp.openInEditor')}</button>}
          <button type="button" className="app-btn" disabled={!script} onClick={copyScript}><FiCopy size={12} /> {t('common:scmp.copy')}</button>
          <button type="button" className="app-btn" disabled={!script} onClick={downloadScript}><FiDownload size={12} /> {t('common:scmp.download')}</button>
        </span>
      </div>
      <span className="stn-note">{t('common:scmp.scriptHint')}</span>
      {script && <span className="stn-note">{t('common:scmp.scriptCounts', { n: script.counts.statements, c: script.counts.commented })}</span>}
      <pre className="stn-pre scmp-script mono">{script ? script.text : t('common:scmp.loading')}</pre>
    </div>
  );

  const body = { target: renderTarget, objects: renderObjects, results: job ? renderResults : null, script: job ? renderScript : null }[step];

  return (
    <AppModal windowed open={open} onClose={onClose} title={t('menu:tools.schemaCompare')} icon={<FiColumns size={14} />} className="app-modal-default stn-modal">
      <div className="stn-body">
        <aside className="stn-side">
          <div className="stn-row stn-report-mode sqq-side-switch" role="tablist" aria-label={t('common:scmp.sideSwitch')}>
            {['jobs', 'criteria'].map(s => (
              <button key={s} type="button" role="tab" aria-selected={side === s} className={`app-btn${side === s ? ' on' : ''}`} onClick={() => setSide(s)}>
                {s === 'jobs' ? <FiColumns size={12} /> : <FiSliders size={12} />} {t(`common:scmp.side.${s}`)}
              </button>
            ))}
          </div>
          <button type="button" className="app-btn app-btn-primary stn-full" onClick={() => startNew()}><FiPlus size={12} /> {t('common:scmp.newJob')}</button>
          {side === 'jobs' ? (
            <ul className="stn-jobs sqq-fade" key="jobs">
              {jobs.map(j => (
                <li key={j.id} className={job && job.id === j.id ? 'on' : ''} onClick={() => openJob(j.id)}>
                  <div className="stn-job-title">{j.title}</div>
                  <div className="stn-job-meta">{t('common:scmp.jobMeta', { n: fmt(j.diffs), at: storedTime(j.updatedAt, 'date') })}</div>
                </li>
              ))}
              {!jobs.length && <li className="stn-empty">{t('common:scmp.noJobs')}</li>}
            </ul>
          ) : (
            <ul className="stn-jobs sqq-fade" key="criteria">
              {criteriaList.map(c => (
                <li key={c.id} className={criteriaId === c.id ? 'on' : ''} onClick={() => loadCriteria(c)}>
                  <div className="stn-job-title scmp-crit-title">
                    <span>{c.name}</span>
                    <button type="button" className="app-btn scmp-crit-del" title={t('common:ui.delete')} aria-label={t('common:ui.delete')} onClick={(ev) => { ev.stopPropagation(); removeCriteria(c); }}><FiTrash2 size={11} /></button>
                  </div>
                  <div className="stn-job-meta">{(c.spec.source || {}).owner} → {(c.spec.target || {}).owner} · {(c.spec.types || []).map(ty => t(`common:scmp.type.${ty}`)).join('·')}</div>
                </li>
              ))}
              {!criteriaList.length && <li className="stn-empty">{t('common:scmp.noCriteriaHint')}</li>}
            </ul>
          )}
        </aside>
        <section className="stn-main">
          {/* 저장 = 비교 기준(대상·범위·오브젝트 선택)으로 저장 — 고르는 단계(①②)에서만. 결과·스크립트는 비교할 때 서버가 남긴다 */}
          <WorkflowHead title={job ? job.title : t('common:scmp.newTitle')}
            meta={job ? t('common:scmp.headLine', { a: job.source.owner, b: job.target.owner, n: fmt(Object.values(job.summary || {}).reduce((x, s) => x + s.diff + s.onlySource + s.onlyTarget, 0)) }) : t('common:scmp.intro')}
            onSave={step === 'target' || step === 'objects' ? saveAsCriteria : null} saveDisabled={!ready} saveLabel={t('common:scmp.saveCriteria')}
            saveTitle={t('common:scmp.saveCriteria')}
            onDelete={job ? removeJob : null} />
          <WorkflowRibbon tabs={TABS} step={step} onStep={setStep} label={s => t(`common:scmp.steps.${s}`)} aside={[]}
            disabledOf={s => !job && (s === 'results' || s === 'script')} />
          <div className="srv-stepHelp" key={`help-${step}`}>{t(`common:scmp.stepHelp.${step}`)}</div>
          <div className="stn-step-wrap" key={`${job ? job.id : 'new'}-${step}`}>{body ? body() : null}</div>
        </section>
      </div>
    </AppModal>
  );
};

SchemaCompareModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  connections: PropTypes.arrayOf(PropTypes.object),
  defaultConnectionId: PropTypes.string,
  /** 스크립트를 편집기 새 탭으로 (text) — 실행은 사람이 */
  onOpenInEditor: PropTypes.func,
  restore: PropTypes.shape({ id: PropTypes.string, step: PropTypes.string }),
  onView: PropTypes.func,
};

// 편집기에 글자를 칠 때마다 App 이 다시 그려져도 값이 같으면 다시 그리지 않는다(common/memoWindow, 2026-10-26 — 입력 지연)
export default memoWindow(SchemaCompareModal);
